import { useApolloClient, useMutation } from '@apollo/client';
import { useCallback } from 'react';
import { Fragments, Mutations } from '@shared/api';
import { API } from '@shared/types/api';
import { notNil } from '@shared/util/notNil';
import { toComponentOneOf } from '@shared/util/workflow';

type requiredWorkflowTemplateProperties =
  | 'components'
  | 'archivedComponents'
  | 'name'
  | 'requiresName'
  | 'behavior'
  | 'channelDefaultConfiguration';

export type WorkflowTemplateUpdateData = Pick<
  API.WorkflowTemplateFragment,
  'id' | requiredWorkflowTemplateProperties
>;

export function useUpdateWorkflowTemplate() {
  const apolloClient = useApolloClient();
  const [updateWorkflow, { loading, error }] = useMutation<
    API.UpdateWorkflowTemplateMutation,
    API.UpdateWorkflowTemplateMutationVariables
  >(Mutations.workflow.updateWorkflow);

  return [
    useCallback(
      async (workflowTemplate: WorkflowTemplateUpdateData) => {
        const cacheId = `WorkflowTemplate${workflowTemplate.id}`;
        const cachedData = apolloClient.readFragment<API.WorkflowTemplateFragment>({
          id: cacheId,
          fragment: Fragments.workflow.WorkflowTemplate,
          fragmentName: 'WorkflowTemplate',
        });
        return updateWorkflow({
          variables: {
            input: {
              id: workflowTemplate.id,
              name: workflowTemplate.name,
              requiresName: workflowTemplate.requiresName,
              components: workflowTemplate.components.map(toComponentOneOf).filter(notNil),
              archivedComponents: workflowTemplate.archivedComponents
                ?.map(toComponentOneOf)
                .filter(notNil),
            },
          },
          optimisticResponse: {
            updateWorkflowTemplate: {
              ...cachedData,
              ...workflowTemplate,
            } as API.WorkflowTemplateFragment,
          },
        });
      },
      [updateWorkflow, apolloClient],
    ),
    { loading, error },
  ] as const;
}
